/**
 * The vertical scroller a play widget would use on its own.
 *
 * In a FlashBook there is exactly one vertical scroller: the page. A widget that
 * scrolls itself turns the page into a stack of nested boxes — the wheel catches
 * on whichever block is under the pointer, and a long question is presented as a
 * cut-off window (with its own scrollbar) instead of as paper that simply
 * continues below.
 *
 * So inside a book the widget prints: the viewport box and its content box, in
 * flow, with no scrolling of their own. Outside a book — a widget rendered on a
 * screen of its own — it keeps its own scroller, because there the widget *is*
 * the page.
 */
import {type ReactNode} from 'react'
import {ScrollView, StyleSheet, View} from 'react-native'
import {type StyleProp, type ViewStyle} from 'react-native'

import {useIsFlashBookPlay} from '#/components/flashbooks/FlashBookPlayTheme'

type Props = {
  style?: StyleProp<ViewStyle>
  contentContainerStyle?: StyleProp<ViewStyle>
  keyboardShouldPersistTaps?: 'always' | 'never' | 'handled'
  children: ReactNode
}

export function PlayScroller({
  style,
  contentContainerStyle,
  keyboardShouldPersistTaps,
  children,
}: Props) {
  const inBook = useIsFlashBookPlay()

  if (inBook) {
    return (
      <View style={[style, styles.print]}>
        <View style={contentContainerStyle}>{children}</View>
      </View>
    )
  }

  return (
    <ScrollView
      style={style}
      contentContainerStyle={contentContainerStyle}
      keyboardShouldPersistTaps={keyboardShouldPersistTaps}
      showsVerticalScrollIndicator={false}>
      {children}
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  // The page's own scroller already owns the vertical axis; a block only has to
  // stop asking for it. `flex: 1` stays — inside a content-sized parent it just
  // means "as tall as my content", which is what a printed block is.
  print: {minHeight: 0},
})
